import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";

import useToast from "../../hooks/useToast";
import { TOAST } from "../../constants/ToastMessages";

import { PageHeader, LabelM, SubtitleM } from "../../components/Typography";
import useApiCall from "../../hooks/useApiCall";

const LABELM_STYLE_EXT = { fontSize: "1rem", textAlign: "left", marginBottom: "8px" };

const CAPABILITY_TYPE_COLORS = {
  http_call: "primary",
  rag_api_tool: "info",
  mcp_client: "success",
  llm_tool: "secondary",
};

export default function AgentCapabilitiesTab() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { success, error } = useToast();

  // Detect if we're viewing a template or an instance
  const isTemplate = location.pathname.includes('/admin/agent-templates/');

  const [agent, setAgent] = useState(null);
  const [agentCapabilities, setAgentCapabilities] = useState([]);
  const [availableCapabilities, setAvailableCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [capabilitySelectorOpen, setCapabilitySelectorOpen] = useState(false);
  const [editingCapability, setEditingCapability] = useState(null);
  const [selectedCapabilityIds, setSelectedCapabilityIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [capabilityToDetach, setCapabilityToDetach] = useState(null);
  const [formData, setFormData] = useState({
    capability_id: "",
    capability_instance_name: "",
    enabled: true,
    priority: 1,
    configuration_overrides: {
      tools_input: [
        {
          key: "eg. order_id",
          key_type: "string",
          default: ""
        }
      ],
      timeout: 30,
      retry_count: 3
    },
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use different endpoint based on whether it's a template or instance
      const agentEndpoint = isTemplate
        ? `/v2/agent_templates/${id}`
        : `/v2/agent_instances/${id}`;

      const [agentResp, toolsResp, availableResp] = await Promise.all([
        makeRequest(agentEndpoint, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest(`/v2/agents/${id}/capabilities`, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest('/v2/capabilities?status=active', {
          method: 'GET',
          authTokens: tokens,
        }),
      ]);

      // Handle both template and instance response formats
      const agentData = agentResp.template || agentResp.agent || agentResp;
      setAgent(agentData);
      setAgentCapabilities(toolsResp.agent_capabilities || []);
      setAvailableCapabilities(availableResp.capabilities || []);

    } catch {
      // error already toasted by useApiCall
    } finally {
      setLoading(false);
    }
  };

  const handleOpenToolSelector = () => {
    setSelectedCapabilityIds([]);
    setCapabilitySelectorOpen(true);
  };

  const handleCloseToolSelector = () => {
    setCapabilitySelectorOpen(false);
    setSelectedCapabilityIds([]);
  };

  const handleCapabilityToggle = (capabilityId) => {
    setSelectedCapabilityIds((prev) =>
      prev.includes(capabilityId)
        ? prev.filter((id) => id !== capabilityId)
        : [...prev, capabilityId]
    );
  };

  const handleAttachSelectedTools = async () => {
    if (selectedCapabilityIds.length === 0) {
      error(TOAST.capability.selectAtLeastOne);
      return;
    }

    try {
      for (const capabilityId of selectedCapabilityIds) {
        const capability =availableCapabilities.find((t) => t.id === capabilityId);
        await makeRequest(`/v2/agents/${id}/capabilities`, {
          method: 'POST',
          body: {
            agent_capability: {
              capability_id: capabilityId,
              capability_instance_name: capability.name,
              enabled: true,
              priority: agentCapabilities.length + 1,
              configuration_overrides: {},
            },
          },
          authTokens: tokens,
        });
      }
      success(TOAST.capability.attached(selectedCapabilityIds.length));
      handleCloseToolSelector();
      fetchData();
    } catch {
      // error already toasted by useApiCall
    }
  };

  const handleOpenEditDialog = (agentCapability = null) => {
    if (agentCapability) {
      setEditingCapability(agentCapability);

      // Use sample configuration if overrides are empty or not set
      const hasOverrides = agentCapability.configuration_overrides &&
        Object.keys(agentCapability.configuration_overrides).length > 0;

      setFormData({
        capability_id: agentCapability.capability_id,
        capability_instance_name: agentCapability.capability_instance_name,
        enabled: agentCapability.enabled,
        priority: agentCapability.priority,
        configuration_overrides: hasOverrides
          ? agentCapability.configuration_overrides
          : {
              tools_input: [
                {
                  key: "eg. order_id",
                  key_type: "string",
                  default: ""
                }
              ],
              timeout: 30,
              retry_count: 3
            },
      });
    }
    setDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setDialogOpen(false);
    setEditingCapability(null);
    setFormData({
      capability_id: "",
      capability_instance_name: "",
      enabled: true,
      priority: 1,
      configuration_overrides: {
        tools_input: [
          {
            key: "eg. order_id",
            key_type: "string",
            default: ""
          }
        ],
        timeout: 30,
        retry_count: 3
      },
    });
  };

  const handleSaveToolConfig = async () => {
    if (!formData.capability_instance_name) {
      error(TOAST.capability.instanceNameRequired);
      return;
    }

    try {
      // Prepare configuration_overrides
      const configOverrides = { ...formData.configuration_overrides };

      await makeRequest(`/v2/agents/${id}/capabilities/${editingCapability.id}`, {
        method: 'PUT',
        body: {
          agent_capability: {
            capability_instance_name: formData.capability_instance_name,
            enabled: formData.enabled,
            priority: formData.priority,
            configuration_overrides: configOverrides,
          },
        },
        authTokens: tokens,
      });
      success(TOAST.capability.configUpdated);
      handleCloseEditDialog();
      fetchData();
    } catch {
      // error already toasted by useApiCall
    }
  };

  const handleDetachClick = (capabilityId, instanceName) => {
    setCapabilityToDetach({ capabilityId, instanceName });
    setDeleteConfirmOpen(true);
  };

  const handleDetach = async () => {
    if (!capabilityToDetach) return;

    const { capabilityId, instanceName } = capabilityToDetach;
    setDeleteConfirmOpen(false);

    try {
      await makeRequest(`/v2/agents/${id}/capabilities/${capabilityId}`, {
        method: 'DELETE',
        authTokens: tokens,
      });
      success(TOAST.capability.detached);
      fetchData();
    } catch {
      // error already toasted by useApiCall
    }
  };

  // Filter out already attached capabilities
  const getUnattachedCapabilities = () => {
    const attachedCapabilityIds = agentCapabilities.map((at) => at.capability_id);
    return availableCapabilities.filter((capability) => !attachedCapabilityIds.includes(capability.id));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageHeader>
        Capability Instances: {agent?.name}
      </PageHeader>
      <SubtitleM>
        Attach capability templates and configure instances for this agent
      </SubtitleM>

      {/* Action Bar */}
      <Box sx={{ display: "flex", gap: 2, my: 3 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate("/admin/agent-templates")}
        >
          Back to Templates
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenToolSelector}
        >
          Attach Capability Templates
        </Button>
      </Box>

      {/* Capabilities Table */}
      {agentCapabilities.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <SubtitleM>No capability instances attached yet</SubtitleM>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenToolSelector}
            sx={{ mt: 2 }}
          >
            Attach Your First Capability Template
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Instance Name</TableCell>
                <TableCell>Capability Name</TableCell>
                <TableCell>Capability Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Has Overrides</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agentCapabilities
                .sort((a, b) => a.priority - b.priority)
                .map((agentCapability) => (
                  <TableRow key={agentCapability.id} hover>
                    <TableCell>
                      <strong>{agentCapability.capability_instance_name}</strong>
                    </TableCell>
                    <TableCell>{agentCapability.capability?.name || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={agentCapability.capability?.capability_type || "unknown"}
                        color={
                          CAPABILITY_TYPE_COLORS[agentCapability.capability?.capability_type] ||
                          "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{agentCapability.priority}</TableCell>
                    <TableCell>
                      <Chip
                        label={agentCapability.enabled ? "Enabled" : "Disabled"}
                        color={agentCapability.enabled ? "success" : "default"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {Object.keys(
                        agentCapability.configuration_overrides || {}
                      ).length > 0 ? (
                        <Chip label="Yes" color="primary" size="small" />
                      ) : (
                        <Chip label="No" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Configuration">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(agentCapability)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Detach">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDetachClick(
                              agentCapability.id,
                              agentCapability.capability_instance_name
                            )
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Capability Selector Dialog */}
      <Dialog
        open={capabilitySelectorOpen}
        onClose={handleCloseToolSelector}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Capability Templates to Attach</DialogTitle>
        <DialogContent>
          {getUnattachedCapabilities().length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                All available capability templates are already attached
              </Typography>
            </Box>
          ) : (
            <List>
              {getUnattachedCapabilities().map((capability) => (
                <React.Fragment key={capability.id}>
                  <ListItemButton onClick={() => handleCapabilityToggle(capability.id)}>
                    <Checkbox
                      checked={selectedCapabilityIds.includes(capability.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {capability.display_name || capability.name}
                          <Chip
                            label={capability.capability_type}
                            size="small"
                            color={CAPABILITY_TYPE_COLORS[capability.capability_type] || "default"}
                          />
                        </Box>
                      }
                      secondary={capability.description}
                    />
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseToolSelector}>Cancel</Button>
          <Button
            onClick={handleAttachSelectedTools}
            variant="contained"
            disabled={selectedCapabilityIds.length === 0}
          >
            Attach {selectedCapabilityIds.length > 0 && `(${selectedCapabilityIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Capability Configuration Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Capability Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <Box>
              <LabelM style={LABELM_STYLE_EXT}>Capability Instance Name *</LabelM>
              <TextField
                fullWidth
                value={formData.capability_instance_name}
                onChange={(e) =>
                  setFormData({ ...formData, capability_instance_name: e.target.value })
                }
                placeholder="e.g., my_weather_api"
              />
            </Box>

            <Box>
              <LabelM style={LABELM_STYLE_EXT}>Priority</LabelM>
              <TextField
                fullWidth
                type="number"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: parseInt(e.target.value) })
                }
                inputProps={{ min: 1 }}
              />
            </Box>

            <Box>
              <LabelM style={LABELM_STYLE_EXT}>Status</LabelM>
              <TextField
                select
                fullWidth
                value={formData.enabled}
                onChange={(e) =>
                  setFormData({ ...formData, enabled: e.target.value === "true" })
                }
              >
                <MenuItem value="true">Enabled</MenuItem>
                <MenuItem value="false">Disabled</MenuItem>
              </TextField>
            </Box>

            {/* Capability Input Arguments */}
            <Box>
              <LabelM style={LABELM_STYLE_EXT}>
                Capability Input Arguments (JSON)
              </LabelM>
              <SubtitleM style={{ marginBottom: "8px", color: "#666", fontSize: "0.85rem" }}>
                Override input parameters for this agent instance
              </SubtitleM>
              <TextField
                fullWidth
                multiline
                rows={5}
                value={
                  typeof formData.configuration_overrides?.tools_input === "object"
                    ? JSON.stringify(formData.configuration_overrides.tools_input, null, 2)
                    : formData.configuration_overrides?.tools_input || ""
                }
                onChange={(e) => {
                  try {
                    const parsed = e.target.value ? JSON.parse(e.target.value) : {};
                    setFormData({
                      ...formData,
                      configuration_overrides: {
                        ...formData.configuration_overrides,
                        tools_input: parsed,
                      },
                    });
                  } catch {
                    // Keep the string value if JSON parsing fails
                    setFormData({
                      ...formData,
                      configuration_overrides: {
                        ...formData.configuration_overrides,
                        tools_input: e.target.value,
                      },
                    });
                  }
                }}
                placeholder='{\n  "location": {\n    "type": "string",\n    "description": "City name"\n  }\n}'
                sx={{ fontFamily: "monospace" }}
              />
            </Box>

            <Box>
              <LabelM style={LABELM_STYLE_EXT}>
                Configuration Overrides (JSON)
              </LabelM>
              <SubtitleM style={{ marginBottom: "8px", color: "#666", fontSize: "0.85rem" }}>
                Override other capability configuration for this agent
              </SubtitleM>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={
                  typeof formData.configuration_overrides === "object"
                    ? JSON.stringify(formData.configuration_overrides, null, 2)
                    : formData.configuration_overrides
                }
                onChange={(e) => {
                  try {
                    const parsed = e.target.value ? JSON.parse(e.target.value) : {};
                    setFormData({ ...formData, configuration_overrides: parsed });
                  } catch {
                    setFormData({
                      ...formData,
                      configuration_overrides: e.target.value,
                    });
                  }
                }}
                placeholder='{\n  "timeout": 30,\n  "retry_count": 3\n}'
                sx={{ fontFamily: "monospace" }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleSaveToolConfig} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Detach</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to detach capability "{capabilityToDetach?.instanceName}" from this agent?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDetach} variant="contained" color="error">
            Detach
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
